// SYNC: near-identical across cres-t1/t4/t5/t6 (only the 401-refresh authenticate path differs per
// app) — keep in sync. The assistant backend is centralized in cres-aws-t2.
import axios from "axios";
import * as Analytics from "./Analytics";
import {SessionContext} from "./SessionContext";

// Cross-app axios instance for calling the Developer (t2) backend from the Data Catalog (t5) app.
// Mirrors AxiosRestAPI (the t3 cross-app instance): same interceptors, but pointed at the Developer
// API host. We present THIS app's t5 access token — t2's API-Gateway authorizer already accepts a
// `t5.user.access` token (resolves identity via Data Catalog → Get Authorization Context) and the
// platform DB is shared, so the AI assistant running in the t2 Lambda operates on the same data.
// Used by src/data/assistant.ts to reach the centralized ParkourAI assistant at t2/v1/assistant.
export function makeAxiosDeveloperAPI(sessionContext: any) {

    // may be a dict, null or a SessionContext object depending on login completion status
    if (!sessionContext) sessionContext = new SessionContext();

    const instance = axios.create({
        baseURL: process.env.REACT_APP_DEVELOPER_API_URL,
    });

    instance.interceptors.request.use(
        (config: any) => {
            if (config.metadata === undefined) {
                config.metadata = {};
                config.metadata.startTime = null;
                config.metadata.endTime = null;
            }
            config.metadata.startTime = new Date();

            Analytics.record("API Request", {
                "HTTP URL": config.url,
                "HTTP Method": config.method,
            });

            const token = sessionContext.getAccessToken();
            if (token) config.headers["X-Cres-AccessToken"] = token;

            return config;
        },
        (error) => Promise.reject(error),
    );

    instance.interceptors.response.use(
        (response: any) => {

            // Sometime response.data is string instead of object #1723
            // https://github.com/axios/axios/issues/1723
            if (response.headers["content-type"]?.includes("application/json")) {
                if (typeof response.data === "string" && response.data !== "") {
                    try {
                        response.data = JSON.parse(
                            response.data
                                .replace(/\\/g, "\\\\")
                                .replace(/\n/g, "\\n")
                                .replace(/\r/g, "\\r")
                                .replace(/\\"/g, "\""),
                        );
                    } catch (e) {
                        console.error(e);
                    }
                }
            }

            const config = response.config;
            config.metadata.endTime = new Date();

            Analytics.record("API Response", {
                "HTTP URL": config.url,
                "HTTP Method": config.method,
                "HTTP Code": response.status,
                "HTTP Duration": (config.metadata.endTime - config.metadata.startTime) / 1000,
                "Reason Code": response.data ? response.data.Code : undefined,
            });

            return response;
        },
        (error) => {

            const config = error.config;
            const response = error.response;

            config.metadata.endTime = new Date();

            // response can be undefined when there is a network issue / no connection
            if (response === undefined) {
                return Promise.reject(error);
            }

            Analytics.record("API Response", {
                "HTTP URL": config.url,
                "HTTP Method": config.method,
                "HTTP Code": response.status,
                "HTTP Duration": (config.metadata.endTime - config.metadata.startTime) / 1000,
                "Reason Code": response.data ? response.data.Code : undefined,
            });

            // On a 401, refresh THIS app's (t5) token via t5's own authenticate, then retry against t2.
            if (config.metadata.disableRefresh !== true) {
                if (config.metadata.cogntioRefreshed === undefined) {
                    config.metadata.cogntioRefreshed = true;

                    if (response.status === 401) {
                        return axios({
                            method: "PUT",
                            // THIS TIER'S authenticate, not the Data Catalog's. The client below
                            // talks to t2, but the token it refreshes is this app's own -- and
                            // REACT_APP_API_URL now means t10, so the old path resolved to
                            // api.reports.../t5/v1/authenticate and would have 403'd. A second
                            // axios instance holding its own copy of the refresh URL is exactly
                            // how the result grid's EnterpriseComponent was missed earlier.
                            url: `${process.env.REACT_APP_API_URL}/t10/v1/authenticate`,
                            data: {
                                "X-Cres-RefreshToken": sessionContext.getRefreshToken(),
                            },
                        }).then((response) => {
                            const token = response.data.AccessToken;
                            sessionContext.setAccessToken(token);
                            config.headers["X-Cres-AccessToken"] = token;
                            // Retry through `instance`, NOT raw axios: the response interceptor must run on the retry
                            // too, or its JSON-string repair is skipped and callers silently receive an unparsed body.
                            // No loop risk: cogntioRefreshed is already set, so a second 401 falls through to reject.
                            return instance(config);
                        });
                    }
                }
            }

            return Promise.reject(error);
        },
    );

    return instance;
}

const AxiosDeveloperAPI = makeAxiosDeveloperAPI(null);

export default AxiosDeveloperAPI;
